import React, { useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';

import { HashLink as Link } from 'react-router-hash-link';

// Keyframes for a subtle glow animation
const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(106, 27, 154, 0.3); }
  50% { box-shadow: 0 0 15px rgba(106, 27, 154, 0.5); }
  100% { box-shadow: 0 0 5px rgba(106, 27, 154, 0.3); }
`;

// Styled components for custom design
const SignInContainer = styled(Box)(({ theme }) => ({
    minHeight: '80vh',
    background: 'linear-gradient(135deg,rgb(21, 175, 195) 0%, #e1bee7 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
}));

const SignInCard = styled(Box)(({ theme }) => ({
    maxWidth: 400,
    width: '100%',
    padding: theme.spacing(4),
    background: 'white',
    borderRadius: theme.spacing(2),
    boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(45deg, #6a1b9a, #ab47bc)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    animation: `${glow} 3s infinite`,
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
    },
}));

const GradientTypography = styled(Typography)(({ theme }) => ({
    background: 'linear-gradient(45deg, #6a1b9a 30%, #ab47bc 90%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1),
        '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
        },
    },
    '& .MuiInputLabel-root': {
        color: theme.palette.grey[600],
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(45deg, #6a1b9a 30%, #ab47bc 90%)',
    color: 'white',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
    '&:hover': {
        background: 'linear-gradient(45deg, #4a0072 30%, #8e24aa 90%)',
        transform: 'scale(1.02)',
        transition: 'all 0.3s ease',
    },
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    opacity: 0,
    animation: 'fadeIn 0.5s forwards',
    '@keyframes fadeIn': {
        to: { opacity: 1 },
    },
}));

const AdminSignIn = () => {
    const [form, setForm] = useState({
        email: '',
        password: '',
    });
    const [error, setError] = useState('');

    const history = useHistory();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const loginUrl = process.env.REACT_APP_SERVER
            ? 'https://salonserver-k8m8.onrender.com/login'
            : 'http://localhost:3001/login';
        try {
            const response = await axios.post(loginUrl, {
                user: {
                    email: form.email,
                    password: form.password,
                },
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });

            const user = response.data.data;
            console.log('Admin Sign-in successful:', response.data);

            if (user.usertype !== 'admin') {
                setError('Access denied. Only admin users can sign in here.');
                return;
            }
            const token = response.headers['authorization'];
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            history.push('/admin');
        } catch (error) {
            console.error('Sign-in error:', error.response ? error.response.data : error.message);
            setError(error.response?.data?.error || 'Sign-in failed. Please check your credentials.');
        }
    };

    return (
        <SignInContainer>
            <SignInCard>
                <GradientTypography variant="h5">
                    Admin Sign-In
                </GradientTypography>
                {error && (
                    <StyledAlert severity="error">
                        {error}
                    </StyledAlert>
                )}
                <Box component="form" onSubmit={handleSubmit}>
                    <StyledTextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        variant="outlined"
                        required
                        sx={{ mb: 3 }}
                    />
                    <StyledTextField
                        fullWidth
                        label="Password"
                        name="password"
                        type="password"
                        value={form.password}
                        onChange={handleChange}
                        variant="outlined"
                        required
                        sx={{ mb: 3 }}
                    />
                    <StyledButton type="submit" fullWidth>
                        Sign In
                    </StyledButton>
                </Box>
            </SignInCard>
            <Link
                to={'/ForgotPassword'}
                color='black'
            // onClick={
            //     onCloseLoginModal
            // }
            >
                Forgot Password
            </Link>
        </SignInContainer>
    );
};

export default AdminSignIn;

